import React, { useEffect, useState, useCallback } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import { withRouter } from 'react-router-dom';
import LoyaltyCard from '../loyalty-v2/LoyaltyCard';
import { isDarkColor } from '../gift-cards-v2/CustomCardScene';
import { themeById } from '../loyalty-v2/data';
import { walletService } from '../../_services/wallet.service';
import { hapticSuccess } from '../../_utils/haptics';
import './StampEarnedCelebration.css';

/**
 * StampEarnedCelebration
 * The client-side "your stamp landed" moment. Mounted once at router level so
 * it fires wherever the customer opens the app (Explore or any /dashboard
 * screen), not just on the wallet.
 *
 * How it knows: the loyalty wallet is the source of truth for progress, so we
 * keep a per-scheme "last seen" figure in localStorage and celebrate when the
 * real figure has moved past it. No backend change and no push needed, and it
 * survives the client not opening the app for weeks.
 *
 * Deliberately quiet in three cases:
 *   - first ever run: seed the baseline silently, so we never celebrate stamps
 *     the client already knew about
 *   - progress went DOWN (a redemption or a reversed booking): reseed, no popup
 *   - already celebrated this figure: baseline matches, nothing to say
 */

// Only the customer-facing surfaces. Keeps the popup off the diary/POS if a
// salon user happens to hold a loyalty card of their own.
const CLIENT_ROUTES = [
  (p) => p === '/',
  (p) => p.startsWith('/dashboard'),
];
const onClientRoute = (pathname) => CLIENT_ROUTES.some((m) => m(pathname));

const seenKey = (schemeId) => `beu-stamp-seen-${localStorage.getItem('userId') || 'me'}-${schemeId}`;

const readSeen = (schemeId) => {
  try {
    const raw = localStorage.getItem(seenKey(schemeId));
    return raw === null ? null : Number(raw);
  } catch (e) { return null; }
};
const writeSeen = (schemeId, value) => {
  try { localStorage.setItem(seenKey(schemeId), String(value)); } catch (e) { /* ignore */ }
};

// Stamps count stamps; points count points. One number to compare either way.
const progressOf = (card) => {
  const st = card.state || {};
  return (card.scheme || {}).mode === 'points' ? (st.balance || 0) : (st.filled || 0);
};

/**
 * Preview: add ?preview=reward (or ?preview=stamp) to any customer route to
 * see the celebration on demand, e.g. /dashboard/wallet?preview=reward. Add
 * &theme=rose to check it against another card theme. Purely a dummy card:
 * it never reads the wallet and never writes a "seen" figure, so previewing
 * can't swallow a real client's celebration.
 */
const previewCard = (mode, theme, points) => ({
  schemeId: '__preview__',
  salonName: 'Professional Beauty',
  design: {
    themeId: theme || 'noir', icon: 'sparkle', layout: 'grid',
    fillStyle: 'stamp-ink', orientation: 'landscape', stampShape: 'circle', header: 'classic',
  },
  scheme: points
    ? { mode: 'points', pointsTarget: 500, pointsLabel: 'Free treatment' }
    : { mode: 'stamps', stampsRequired: 4, rewardLabel: 'Free blow dry' },
  state: mode === 'reward'
    ? (points ? { balance: 500, rewardsAvailable: 1 } : { filled: 4, rewardsAvailable: 1 })
    : (points ? { balance: 320, rewardsAvailable: 0 } : { filled: 2, rewardsAvailable: 0 }),
});

// Mix two hex colours; t is the share of the first. Used to tint the
// celebration backdrop with the card's own accent so the moment feels
// on-brand for the salon while the ground stays dark enough for white type.
const parseHex = (h) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(h || '').trim());
  return m ? parseInt(m[1], 16) : null;
};
const mixHex = (a, b, t) => {
  const pa = parseHex(a);
  const pb = parseHex(b);
  if (pa === null || pb === null) return b;
  const ch = (shift) => Math.round(((pa >> shift) & 255) * t + ((pb >> shift) & 255) * (1 - t));
  return `rgb(${ch(16)}, ${ch(8)}, ${ch(0)})`;
};
// Perceived lightness, 0–1. Decides whether a light card's room should be
// paler than the card or deeper than it.
const lightnessOf = (hex) => {
  const p = parseHex(hex);
  if (p === null) return 1;
  return (0.2126 * ((p >> 16) & 255) + 0.7152 * ((p >> 8) & 255) + 0.0722 * (p & 255)) / 255;
};

/**
 * The "card is full" moment itself, without the wallet-watching around it, so
 * the salon can preview the exact screen its clients get (see LoyaltyContent's
 * Preview a visit). Actions differ per caller: the client goes to their
 * wallet, the salon just closes the preview.
 */
export const RewardUnlockedView = ({ design, scheme, state, salonName, rewardName, subline, caption, actions, zIndex }) => {
  const theme = themeById(design && design.themeId);
  // Same accent the card itself uses, so the glow belongs to that salon.
  const accent = (design && design.stampColor) || theme.accent;
  // The whole stage follows the card: light cards get a light room with
  // dark type and dark buttons, dark cards keep the near-black one. The
  // card's base colour is its custom background when set (gradients judge
  // by their start colour), otherwise the theme paper. Photo backgrounds
  // stay on the dark stage — their brightness is unknowable.
  const custom = (design && design.custom) || null;
  const customBase = custom && custom.bgMode === 'gradient'
    ? (custom.bgGradient && custom.bgGradient.from)
    : (custom && custom.bgMode && custom.bgMode !== 'photo' ? custom.bgColor : null);
  const validHex = (h) => /^#?[0-9a-f]{6}$/i.test(String(h || '').trim());
  const cardBase = validHex(customBase) ? customBase : theme.bg;
  const light = custom && custom.bgMode === 'photo' ? false : !isDarkColor(cardBase);
  // The eyebrow needs contrast against its own stage: on the dark ground a
  // dark accent lifts to white, on the light ground a pale accent drops to ink.
  const labelColour = light
    ? (isDarkColor(accent) ? accent : 'rgba(26,26,30,0.62)')
    : (isDarkColor(accent) ? 'rgba(255,255,255,0.72)' : accent);
  // The stage is built from the card's own colours, always with a vignette
  // that deepens outwards so the card sits in a pool of light.
  //   dark card  → near-black warmed with the accent
  //   light card → a paler wash of the card's own colour
  //   already-pale card (cream, bone) → lightening it further would leave
  //     the card invisible, so the room deepens with the accent instead
  const pale = lightnessOf(cardBase) > 0.8;
  const lightStage = pale
    ? `radial-gradient(120% 90% at 50% 18%, ${mixHex(accent, cardBase, 0.07)} 0%, ${mixHex(accent, cardBase, 0.13)} 55%, ${mixHex(accent, cardBase, 0.22)} 100%)`
    : `radial-gradient(120% 90% at 50% 18%, ${mixHex(cardBase, '#ffffff', 0.16)} 0%, ${mixHex(cardBase, '#ffffff', 0.26)} 55%, ${mixHex(cardBase, '#ffffff', 0.4)} 100%)`;
  const stage = {
    background: light
      ? lightStage
      : `radial-gradient(120% 90% at 50% 18%, ${mixHex(accent, '#1d1d22', 0.18)} 0%, ${mixHex(accent, '#121216', 0.1)} 55%, ${mixHex(accent, '#0c0c0f', 0.05)} 100%)`,
  };
  return (
    <div className={`reward-full${light ? ' reward-full--light' : ''}`} style={zIndex ? { zIndex, ...stage } : stage} role="dialog" aria-modal="true" aria-label={`Reward unlocked: ${rewardName}`}>
      {/* The bloom is whatever reads as light against its own stage: the
          accent out of blackness, white on a pale wash. On an already-pale
          card's deeper room, white would flatten the vignette again, so
          that case keeps the card's own colour as the light. */}
      <div className="reward-full-glow" style={{ background: `radial-gradient(closest-side, ${light ? (pale ? cardBase : '#ffffff') : accent}, transparent 70%)` }} />
      {/* Content centres in the space that's left; the actions stay pinned
          to the bottom so the primary CTA is always thumb-reachable. */}
      <div className="reward-full-body">
        <div className="reward-full-inner">
          <p className="reward-full-eyebrow" style={{ color: labelColour }}>Reward unlocked</p>
          <div className="reward-full-card">
            {/* The sheen hugs the card itself: on the outer flex wrapper it
                spanned the full inner column and overhung a narrower card. */}
            <div className="reward-full-card-hug">
              <LoyaltyCard
                design={design}
                scheme={scheme}
                state={state}
                salonName={salonName}
                width={320}
              />
              <span className="reward-full-sheen" />
            </div>
          </div>
          <h2 className="reward-full-title">{rewardName}</h2>
          <p className="reward-full-sub">{subline}</p>
        </div>
      </div>
      <div className="reward-full-actions">
        {caption && <p className="reward-full-caption">{caption}</p>}
        {actions}
      </div>
    </div>
  );
};

const StampEarnedCelebration = ({ location, history }) => {
  const [card, setCard] = useState(null);      // the card to celebrate
  const [earned, setEarned] = useState(0);      // how much landed since last seen
  const [checked, setChecked] = useState(false); // once per app open
  const [isPreview, setIsPreview] = useState(false);

  const dismiss = useCallback(() => {
    // A preview must not record progress against a real scheme.
    if (card && !isPreview) writeSeen(card.schemeId, progressOf(card));
    setCard(null);
    if (isPreview) {
      setIsPreview(false);
      history.replace(location.pathname); // drop the param so it doesn't reopen
    }
  }, [card, isPreview, history, location.pathname]);

  // Hold the page still behind the celebration, otherwise it reads as a
  // phantom scroll on a full-screen overlay that has nothing to scroll.
  // The celebration appearing is the app's most rewarding moment - pair the
  // visual with a success haptic in the native shell (no-op on web).
  useEffect(() => {
    if (card) hapticSuccess();
  }, [card]);

  // Both html and body need locking: which one is the scrolling element
  // varies, and locking body alone still leaves the document scrollable.
  // Shared ref-counted scroll lock (covers body + root): overlapping
  // overlays must not restore each other's locked styles.
  useScrollLock(!!card);

  useEffect(() => {
    if (card) return;
    if (!onClientRoute(location.pathname)) return;

    const params = new URLSearchParams(location.search || '');
    const mode = params.get('preview');
    if (mode === 'reward' || mode === 'stamp') {
      setCard(previewCard(mode, params.get('theme'), params.get('mode') === 'points'));
      setEarned(1);
      setIsPreview(true);
      return;
    }

    if (checked) return;
    if (!localStorage.getItem('accessToken')) return;
    setChecked(true);

    let alive = true;
    walletService.getMyLoyaltyCards().then((cards) => {
      if (!alive || !Array.isArray(cards) || !cards.length) return;
      let toShow = null;
      let delta = 0;
      cards.forEach((c) => {
        if (!c || !c.schemeId) return;
        const now = progressOf(c);
        const seen = readSeen(c.schemeId);
        if (seen === null || now < seen) {
          // First sight of this card, or progress went backwards (redeemed /
          // reversed). Record where we are and stay quiet.
          writeSeen(c.schemeId, now);
          return;
        }
        if (now > seen && !toShow) { toShow = c; delta = now - seen; }
      });
      if (toShow) { setCard(toShow); setEarned(delta); }
    }).catch(() => { /* never block the app on a wallet hiccup */ });
    return () => { alive = false; };
  }, [location.pathname, location.search, checked, card]);

  if (!card) return null;

  const scheme = card.scheme || {};
  const st = card.state || {};
  const isPoints = scheme.mode === 'points';
  const target = isPoints ? (scheme.pointsTarget || 500) : (scheme.stampsRequired || 6);
  const filled = st.filled || 0;
  const balance = st.balance || 0;
  const rewardReady = (st.rewardsAvailable || 0) > 0
    || (isPoints ? balance >= target : filled >= target);
  const remaining = isPoints ? Math.max(0, target - balance) : Math.max(0, target - filled);
  const rewardName = (isPoints ? scheme.pointsLabel : scheme.rewardLabel) || 'your reward';
  // getMyLoyaltyCards returns the RAW wallet shape (salonName), not the
  // normalised one useWallet builds (salon.name) — accept either.
  const salonName = card.salonName || (card.salon && card.salon.name) || 'your salon';

  /* ── Completing a card is the payoff of the whole scheme, so it gets a
     full-screen moment rather than a modal: the salon's own card, lit by its
     own accent colour, on near-black. Everything else stays a quiet popup. ── */
  if (rewardReady) {
    const goToWallet = () => { dismiss(); history.push('/dashboard/wallet'); };
    return (
      <RewardUnlockedView
        design={card.design}
        scheme={scheme}
        state={st}
        salonName={salonName}
        rewardName={rewardName}
        subline={`Your card is full at ${salonName}. Show this on your next visit to claim it.`}
        actions={(
          <>
            <button type="button" className="reward-full-btn" onClick={goToWallet}>View my card</button>
            <button type="button" className="reward-full-close" onClick={dismiss}>Close</button>
          </>
        )}
      />
    );
  }

  const headline = isPoints
    ? `You earned ${earned.toLocaleString()} point${earned === 1 ? '' : 's'}`
    : `You earned ${earned > 1 ? `${earned} stamps` : 'a stamp'}`;

  const subline = isPoints
    ? `${balance.toLocaleString()} of ${target.toLocaleString()} points at ${salonName}, ${remaining.toLocaleString()} to go for ${rewardName}.`
    : `${filled} of ${target} at ${salonName}, ${remaining} more for ${rewardName}.`;

  return (
    <div className="stamp-pop-overlay" onClick={dismiss} role="dialog" aria-modal="true" aria-label={headline}>
      <div className="stamp-pop" onClick={(e) => e.stopPropagation()}>
        <div className="stamp-pop-card">
          <LoyaltyCard
            design={card.design}
            scheme={scheme}
            state={st}
            salonName={salonName}
            width={280}
            /* land the newest stamp with the same pop the salon sees in the designer */
            animateIndex={!isPoints && filled > 0 ? filled - 1 : -1}
          />
        </div>
        <h2 className="stamp-pop-title">{headline}</h2>
        <p className="stamp-pop-sub">{subline}</p>
        <button type="button" className="stamp-pop-btn" onClick={dismiss}>Nice one</button>
      </div>
    </div>
  );
};

export default withRouter(StampEarnedCelebration);
